import { useEffect, useMemo, useRef, useState } from "react";

const backgrounds = ["ef2d56", "ed7d3a", "2fbf71", "363537"];
const luminance = (rgb: number[]) => {
    const scale = [0.2126, 0.7152, 0.0722] as const;
    return rgb
        .map((color) => color / 255)
        .map(
            (color, index) =>
                // https://www.w3.org/TR/WCAG20/#relativeluminancedef
                (color <= 0.03928 ? color / 12.92 : Math.pow((color + 0.055) / 1.055, 2.4)) *
                scale[index],
        )
        .reduce((acc, cur) => acc + cur, 0);
};

const hexToRgb = (hex: string): [number, number, number] =>
    new Array(3).fill(0).map((_val, index) => {
        return parseInt(hex.substring(index * 2, index * 2 + 2), 16);
    }) as [number, number, number];

const foreground = (hex: string) => {
    const hexLuminance = luminance(hexToRgb(hex));
    const blackLuminance = 0;
    const whiteLuminance = 1;
    const blackContrast = (hexLuminance + 0.05) / (blackLuminance + 0.05);
    const whiteContrast = (whiteLuminance + 0.05) / (hexLuminance + 0.05);
    return blackContrast >= whiteContrast ? "000000" : "ffffff";
};

interface GeneratedPfpProps {
    userId: number;
    initiales: string;
}

export default function GeneratedPfp({ userId, initiales }: GeneratedPfpProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const backgroundColor = useMemo(() => backgrounds[userId % backgrounds.length], [userId]);

    const [style, setStyle] = useState({ backgroundColor: `#${backgroundColor}` });
    useEffect(
        () => setStyle((style) => ({ ...style, backgroundColor: `#${backgroundColor}` })),
        [backgroundColor],
    );
    useEffect(() => {
        if (containerRef.current?.clientHeight !== containerRef.current?.clientWidth) {
            console.error("Container of Generated image is not a perfect square");
            const { clientWidth: width, clientHeight: height } = containerRef.current ?? {};
            if (width && height) {
                const size = width > height ? height : width;
                setStyle((style) => ({ ...style, width: `${size}px`, height: `${size}px` }));
            }
        }
    }, [containerRef.current?.clientHeight, containerRef.current?.clientWidth]);

    return (
        <div
            ref={containerRef}
            className="h-full w-full rounded-full text-center justify-center flex flex-col"
            style={style}
        >
            {containerRef.current && (
                <p
                    className="select-none"
                    style={{
                        fontSize: `${containerRef.current.clientHeight * 0.4}px`,
                        color: `#${foreground(backgroundColor)}`,
                    }}
                >
                    {initiales}
                </p>
            )}
        </div>
    );
}
